import React from "react";
import {Flex, Grid, IconButton, Text, Tooltip} from "@chakra-ui/react";
import {FiArrowDown, FiArrowUp, FiTrash2} from "react-icons/fi";
import {BsSoundwave} from "react-icons/bs";
import {FiFlag} from "react-icons/fi";
import {Spot} from "../../components/types";
import {formatMillisecondsAsTimeCode, getCueLabel} from "./cueListHelpers";
import {CUE_LIST_GRID_TEMPLATE} from "./cueListLayout";

const ICON_SLOT_SIZE_PX = 18;

type Props = {
    spot: Spot;
    index: number;
    transitionLabel: string;
    canEdit: boolean;
    isSelected: boolean;
    isDropTarget?: boolean;
    isReorderTarget?: boolean;
    reorderMarkerPosition?: "top" | "bottom";
    isDragging?: boolean;
    isReordering?: boolean;
    onSelect: (spot: Spot, event: React.MouseEvent<HTMLDivElement>) => void;
    onDelete: (spot: Spot) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    onRowDragStart?: (event: React.DragEvent<HTMLDivElement>, spot: Spot) => void;
    onRowDragEnd?: (event: React.DragEvent<HTMLDivElement>, spot: Spot) => void;
    onRowDragOver?: (event: React.DragEvent<HTMLDivElement>, spot: Spot) => void;
    onRowDragLeave?: (event: React.DragEvent<HTMLDivElement>, spot: Spot) => void;
    onRowDrop?: (event: React.DragEvent<HTMLDivElement>, spot: Spot) => void;
    isFirst: boolean;
    isLast: boolean;
    showMoveActions?: boolean;
    showDeleteAction?: boolean;
    deleteTooltipLabel?: string;
    showTranslationFlag?: boolean;
    translationStatusColor?: string;
    translationStatusLabel?: string;
    onOpenTranslations?: (spot: Spot) => void;
    showAudioIndicator?: boolean;
    audioIndicatorColor?: string;
    uploadingFileName?: string;
};

const CueListRow = ({
    spot,
    index,
    transitionLabel,
    canEdit,
    isSelected,
    isDropTarget,
    isReorderTarget,
    reorderMarkerPosition,
    isDragging,
    isReordering,
    onSelect,
    onDelete,
    onMoveUp,
    onMoveDown,
    onRowDragStart,
    onRowDragEnd,
    onRowDragOver,
    onRowDragLeave,
    onRowDrop,
    isFirst,
    isLast,
    showMoveActions = true,
    showDeleteAction = true,
    deleteTooltipLabel = "Delete cue",
    showTranslationFlag = false,
    translationStatusColor,
    translationStatusLabel,
    onOpenTranslations,
    showAudioIndicator = false,
    audioIndicatorColor,
    uploadingFileName
}: Props) => {
    const soundFile = spot.soundFiles?.[0];
    const soundFileName = soundFile?.originalFileName || soundFile?.fileName || "";
    const isUploading = !!uploadingFileName;

    return (
        <Grid
            gridTemplateColumns={CUE_LIST_GRID_TEMPLATE}
            alignItems={"center"}
            px={3}
            py={2}
            borderBottomWidth={"1px"}
            borderColor={isReorderTarget ? "transparent" : "gray.100"}
            cursor={canEdit ? "grab" : "pointer"}
            draggable={canEdit}
            bg={isDropTarget ? "teal.50" : isSelected ? "blue.50" : "white"}
            _hover={{bg: isSelected ? "blue.100" : "gray.50"}}
            boxShadow={isDropTarget
                ? "inset 0 0 0 1px var(--chakra-colors-teal-300)"
                : isReorderTarget
                    ? (reorderMarkerPosition === "bottom"
                        ? "inset 0 -4px 0 var(--chakra-colors-orange-500)"
                        : "inset 0 4px 0 var(--chakra-colors-orange-500)")
                    : undefined}
            opacity={isDragging ? 0.7 : 1}
            onDragStart={(event) => {
                event.stopPropagation();
                onRowDragStart && onRowDragStart(event, spot);
            }}
            onDragEnd={(event) => {
                event.stopPropagation();
                onRowDragEnd && onRowDragEnd(event, spot);
            }}
            onDragOver={(event) => {
                event.stopPropagation();
                onRowDragOver && onRowDragOver(event, spot);
            }}
            onDragLeave={(event) => {
                event.stopPropagation();
                onRowDragLeave && onRowDragLeave(event, spot);
            }}
            onDrop={(event) => {
                event.stopPropagation();
                onRowDrop && onRowDrop(event, spot);
            }}
            onClick={(event) => onSelect(spot, event)}
        >
            <Text fontSize={"sm"} color={"gray.500"} fontWeight={"semibold"}>
                {String(index + 1).padStart(3, "0")}
            </Text>

            <Text fontSize={"sm"} noOfLines={1} fontWeight={isSelected ? "semibold" : "medium"}>
                {getCueLabel(spot) || `Cue ${index + 1}`}
            </Text>

            <Flex align={"center"} gap={1} minW={0}>
                <Flex width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} align={"center"} justify={"center"} flexShrink={0}>
                    {isUploading || soundFileName ? <BsSoundwave size={14}/> : null}
                </Flex>
                <Text
                    fontSize={"sm"}
                    color={isUploading ? "orange.500" : (soundFileName ? "gray.700" : "gray.400")}
                    fontStyle={isUploading ? "italic" : undefined}
                    noOfLines={1}
                >
                    {isUploading ? `${uploadingFileName} uploading…` : (soundFileName || "No sound")}
                </Text>
            </Flex>

            <Text fontSize={"xs"} color={soundFile?.delay ? "gray.700" : "gray.400"}>
                {soundFile?.delay ? formatMillisecondsAsTimeCode(soundFile.delay) : "--"}
            </Text>

            <Text fontSize={"xs"} color={soundFile?.fadeIn ? "gray.700" : "gray.400"}>
                {soundFile?.fadeIn ? formatMillisecondsAsTimeCode(soundFile.fadeIn) : "--"}
            </Text>

            <Text fontSize={"xs"} color={soundFile?.fadeOut ? "gray.700" : "gray.400"}>
                {soundFile?.fadeOut ? formatMillisecondsAsTimeCode(soundFile.fadeOut) : "--"}
            </Text>

            <Text fontSize={"xs"} color={"gray.500"} fontStyle={"italic"} noOfLines={1}>
                {transitionLabel}
            </Text>

            <Flex justifySelf={"center"} align={"center"} gap={1} minH={"24px"} onClick={(event) => event.stopPropagation()}>
                <Flex width={"24px"} height={"24px"} align={"center"} justify={"center"}>
                    {showTranslationFlag ? (
                        <Tooltip label={translationStatusLabel || "Translations"} hasArrow>
                            <IconButton
                                aria-label={"Cue translations"}
                                icon={<FiFlag color={translationStatusColor}/>}
                                size={"xs"}
                                variant={"ghost"}
                                minWidth={"24px"}
                                height={"24px"}
                                onClick={() => onOpenTranslations && onOpenTranslations(spot)}
                            />
                        </Tooltip>
                    ) : null}
                </Flex>
                <Flex width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} align={"center"} justify={"center"}>
                    {showAudioIndicator ? <BsSoundwave size={14} color={audioIndicatorColor}/> : null}
                </Flex>
            </Flex>

            <Flex
                gap={1}
                justifySelf={"end"}
                align={"center"}
                minH={"24px"}
                onClick={(event) => event.stopPropagation()}
            >
                {showMoveActions ? (
                    <>
                        <Tooltip label={"Move up"} hasArrow>
                            <IconButton
                                aria-label={"Move cue up"}
                                icon={<FiArrowUp/>}
                                size={"xs"}
                                variant={"ghost"}
                                onClick={() => onMoveUp(index)}
                                isDisabled={isFirst || !canEdit || !!isReordering}
                            />
                        </Tooltip>
                        <Tooltip label={"Move down"} hasArrow>
                            <IconButton
                                aria-label={"Move cue down"}
                                icon={<FiArrowDown/>}
                                size={"xs"}
                                variant={"ghost"}
                                onClick={() => onMoveDown(index)}
                                isDisabled={isLast || !canEdit || !!isReordering}
                            />
                        </Tooltip>
                    </>
                ) : null}
                {showDeleteAction ? (
                    <Tooltip label={deleteTooltipLabel} hasArrow>
                        <IconButton
                            aria-label={"Delete cue"}
                            icon={<FiTrash2/>}
                            size={"xs"}
                            variant={"ghost"}
                            colorScheme={"red"}
                            onClick={() => onDelete(spot)}
                            isDisabled={!canEdit}
                        />
                    </Tooltip>
                ) : <Flex minH={"24px"} minW={"24px"}/>}
            </Flex>
        </Grid>
    );
};

export default CueListRow;
